import * as React from 'react';
import Text from 'components/Text';
import Row from 'components/Row';
import Time from 'components/Time';
import { Link } from 'components/Link';
import Checkbox from 'components/Checkbox';
import { UpgradePlanQuote } from './AdminUpgradePlanContainer';
import AdminUpgradePlanQuoteBoxRow from './AdminUpgradePlanQuoteBoxRow';
import { AdminUpgradeSharedQuote_sharedQuote_quoteData } from 'admin/queries/__generated__/AdminUpgradeSharedQuote';

import '../styles/AdminUpgradePlanQuoteBox.scss';

export type Props = {
  taxExempt: boolean;
  quote: UpgradePlanQuote | AdminUpgradeSharedQuote_sharedQuote_quoteData;
  onTaxExemptChange: (taxExempt: boolean) => void;
};

export default function AdminUpgradePlanQuoteBox(props: Props) {
  const { quote, taxExempt, onTaxExemptChange } = props;
  const {
    price,
    tax,
    total,
    endDate,
    startDate,
    organization,
    studentsCount,
    teachersCount,
  } = quote;
  const showTax = tax !== '$0.00' || taxExempt;
  const address = [organization?.city, organization?.state].filter(Boolean).join(', ');

  const renderOrganization = () => {
    return (
      <AdminUpgradePlanQuoteBoxRow border data-test="organization">
        <Text size="small">
          <Text weight="semibold">{organization.name},</Text>
          <Text> {address}</Text>
          <Text element="div" weight="light">
            {studentsCount} students, {teachersCount} teachers
          </Text>
        </Text>
        <div></div>
      </AdminUpgradePlanQuoteBoxRow>
    );
  };

  const renderPlan = () => {
    return (
      <AdminUpgradePlanQuoteBoxRow border={showTax} highlighted={!showTax} data-test="plan">
        <div>
          <Row size="xsmall">
            <Text element="div" size="medium" weight="semibold">
              Remind Base Plan
            </Text>
            <Text lineHeight="large" size="small">
              <Time date={new Date(startDate)} month="long" day="numeric" year="numeric" /> -{' '}
              <Time date={new Date(endDate)} month="long" day="numeric" year="numeric" />, with
              automatic renewals
            </Text>
          </Row>
          <Text size="small" weight="light" color="thunder">
            For add-ons such as voice calls, LMS integrations, advanced messaging, or urgent
            messaging, contact sales below.
          </Text>
          <Text size="medium">
            <Link to={'/'}> Learn more</Link>
          </Text>
        </div>
        <Row size="small">
          <Text element="div" size="medium" weight="semibold">
            {price}
          </Text>
        </Row>
      </AdminUpgradePlanQuoteBoxRow>
    );
  };

  const renderTax = () => {
    return (
      <AdminUpgradePlanQuoteBoxRow border data-test="tax">
        <div>
          <Text element="div" size="medium" weight="semibold">
            Estimated sales tax
          </Text>
          <Checkbox
            data-test="tax-toggle"
            checked={taxExempt}
            onChange={() => onTaxExemptChange(!taxExempt)}
            label={
              <Text color="pitch" size="small" weight="normal">
                My organization is exempt from sales tax
              </Text>
            }
          />
        </div>
        <div>
          <Text element="div" size="small">
            {' '}
            {taxExempt ? '--' : tax}{' '}
          </Text>
        </div>
      </AdminUpgradePlanQuoteBoxRow>
    );
  };

  const renderTotal = () => {
    return (
      <AdminUpgradePlanQuoteBoxRow highlighted data-test="total">
        <Text element="div" size="medium" weight="semibold">
          Order Total
        </Text>
        <Text element="div" size="medium" weight="semibold">
          <span data-test="total-price">{taxExempt ? price : total}</span>
        </Text>
      </AdminUpgradePlanQuoteBoxRow>
    );
  };

  return (
    <div className="AdminUpgradePlanQuoteBox">
      {renderOrganization()}
      {renderPlan()}
      {showTax && (
        <>
          {renderTax()}
          {renderTotal()}
        </>
      )}
    </div>
  );
}
